import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ensureUTC } from '../../utils/dateUtils';

const RideHistory = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    riderName: '',
    minDistance: '',
    maxDistance: '',
    minPrice: '',
    maxPrice: '',
    status: '' // '', 'completed', 'cancelled_by_rider', 'cancelled_by_driver'
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalRides: 0,
    totalEarnings: 0,
    totalMiles: 0,
    averagePrice: 0
  });

  useEffect(() => {
    fetchRideHistory();
  }, []);

  useEffect(() => {
    calculateStats(filteredRides);
  }, [filteredRides]);

  const fetchRideHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/rides/history`
      );
      setRides(response.data);
      setFilteredRides(response.data);
    } catch (error) {
      console.error('Error fetching ride history:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = {};
      if (filters.startDate) params.start_date = new Date(filters.startDate).toISOString();
      if (filters.endDate) {
        // Clamp future dates to end of today
        const endDateValue = new Date(filters.endDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const effectiveEndDate = endDateValue > today ? today : endDateValue;
        params.end_date = effectiveEndDate.toISOString();
      }
      if (filters.riderName) params.rider_name = filters.riderName;
      if (filters.minDistance) params.min_distance = parseFloat(filters.minDistance);
      if (filters.maxDistance) params.max_distance = parseFloat(filters.maxDistance);
      if (filters.minPrice) params.min_price = parseFloat(filters.minPrice);
      if (filters.maxPrice) params.max_price = parseFloat(filters.maxPrice);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/rides/history`,
        { params }
      );

      // Apply status filter client-side
      let result = response.data;
      if (filters.status) {
        result = result.filter(r => r.status === filters.status);
      }

      setFilteredRides(result);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      riderName: '',
      minDistance: '',
      maxDistance: '',
      minPrice: '',
      maxPrice: '',
      status: ''
    });
    setFilteredRides(rides);
  };

  const calculateStats = (rideList) => {
    // Include completed and cancelled_by_rider (driver still earns on rider cancellations)
    // Exclude cancelled_by_driver (driver doesn't earn on their own cancellations)
    const earningRides = rideList.filter(r =>
      r.status === 'completed' || r.status === 'cancelled_by_rider'
    );
    // driver_payout already includes net tip, so don't add tip_amount
    const totalEarnings = earningRides.reduce((sum, r) => sum + (r.driver_payout || 0), 0);
    const totalMiles = earningRides.reduce((sum, r) => sum + (r.distance_miles || 0), 0);

    setStats({
      totalRides: earningRides.length,
      totalEarnings: totalEarnings,
      totalMiles: totalMiles,
      averagePrice: earningRides.length > 0 ? totalEarnings / earningRides.length : 0
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Format number with comma separators
  const formatNumber = (num, decimals = 0) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Get today's date in YYYY-MM-DD format (local timezone)
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set a date field to today's date
  const setTodayDate = (fieldName) => {
    setFilters(prev => ({
      ...prev,
      [fieldName]: getTodayDateString()
    }));
  };

  // Format status for display
  const formatStatus = (status) => {
    const statusMap = {
      'completed': 'Completed',
      'cancelled_by_rider': 'Cancelled by Rider',
      'cancelled_by_driver': 'Cancelled by Driver'
    };
    return statusMap[status] || status;
  };

  // Export rides to CSV
  const exportToCSV = () => {
    const sourceData = filteredRides.length > 0 ? filteredRides : rides;

    // Apply status filter or default to earning rides (completed + cancelled_by_rider)
    let dataToExport;
    if (filters.status) {
      dataToExport = sourceData.filter(r => r.status === filters.status);
    } else {
      // Default: include rides where driver earns (matches KPI calculation)
      dataToExport = sourceData.filter(r =>
        r.status === 'completed' || r.status === 'cancelled_by_rider'
      );
    }

    if (dataToExport.length === 0) {
      alert('No rides to export');
      return;
    }

    // CSV Headers
    const headers = [
      'Date',
      'Ride ID',
      'Customer Name',
      'Ride Type',
      'Number of Legs',
      'Total Distance (mi)',
      'Base Fare',
      'Platform Fee',
      'Tip',
      'Taxes',
      'Driver Payout',
      'Status'
    ];

    // Format date for CSV
    const formatDateForCSV = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    // Build CSV rows
    const rows = dataToExport.map(ride => [
      formatDateForCSV(ride.pickup_datetime),
      ride.is_day_booking ? ride.booking_id : ride.id,
      ride.rider_name || 'N/A',
      ride.is_day_booking ? 'Day Booking' : 'Single Ride',
      ride.is_day_booking ? (ride.leg_count || 1) : 1,
      (ride.distance_miles || 0).toFixed(2),
      (ride.total_fare || 0).toFixed(2),
      (ride.platform_commission || ride.platform_fee || 0).toFixed(2),
      (ride.tip_amount || 0).toFixed(2),
      (ride.platform_fee_tax || 0).toFixed(2),
      (ride.driver_payout || 0).toFixed(2),
      formatStatus(ride.status)
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ride-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const calculateTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expires = new Date(expiresAt);
    const hoursRemaining = Math.ceil((expires - now) / (1000 * 60 * 60));

    if (hoursRemaining <= 0) return 'Processing soon...';
    if (hoursRemaining === 1) return '1 hour';
    if (hoursRemaining < 24) return `${hoursRemaining} hours`;
    return '24 hours';
  };

  if (loading && rides.length === 0) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/driver/dashboard')} 
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Ride History</h1>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Total Rides</div>
            <div className="text-3xl font-bold text-blue-600">{formatNumber(stats.totalRides)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Total Earnings
              <span
                className="cursor-help text-gray-400 hover:text-gray-600"
                title="Net earnings after platform fees. Includes completed rides, rider cancellations, both paid out and pending payment."
              >
                ⓘ
              </span>
            </div>
            <div className="text-3xl font-bold text-green-600">${formatNumber(stats.totalEarnings, 2)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Total Miles</div>
            <div className="text-3xl font-bold text-purple-600">{formatNumber(stats.totalMiles, 1)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">Avg per Ride</div>
            <div className="text-3xl font-bold text-orange-600">${formatNumber(stats.averagePrice, 2)}</div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Filter Rides</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setTodayDate('startDate')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-300"
                  >
                    Today
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setTodayDate('endDate')}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-300"
                  >
                    Today
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rider Name
                </label>
                <input
                  type="text"
                  name="riderName"
                  value={filters.riderName}
                  onChange={handleFilterChange}
                  placeholder="Search by rider name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Distance (mi)
                </label>
                <input
                  type="number"
                  name="minDistance"
                  value={filters.minDistance}
                  onChange={handleFilterChange}
                  placeholder="0"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Distance (mi)
                </label>
                <input
                  type="number"
                  name="maxDistance"
                  value={filters.maxDistance}
                  onChange={handleFilterChange}
                  placeholder="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Price ($)
                </label>
                <input
                  type="number"
                  name="minPrice"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  placeholder="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Price ($)
                </label>
                <input
                  type="number"
                  name="maxPrice"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  placeholder="500"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Earning Rides</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled_by_rider">Cancelled by Rider</option>
                  <option value="cancelled_by_driver">Cancelled by Driver</option>
                </select>
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear Filters
              </button>
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Rides
              </button>
            </div>
          </div>
        )}

        {/* Rides List */}
        <div className="bg-white rounded-lg shadow">
          {filteredRides.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-lg">No ride history found</p>
              {Object.values(filters).some(f => f !== '') && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-blue-500 hover:underline"
                >
                  Clear filters to see all rides
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredRides.map(ride => (
                <div key={ride.id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {ride.is_day_booking ? (
                          /* Day booking: show rate type badge and leg count */
                          <>
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                              {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} ({ride.leg_count} {ride.leg_count === 1 ? 'trip' : 'trips'})
                            </span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              ride.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {ride.status}
                            </span>
                          </>
                        ) : (
                          /* Regular ride: show address summary */
                          <>
                            <div className="font-semibold text-lg">
                              {ride.pickup_address || ride.pickup_location} → {ride.dropoff_address || ride.dropoff_location}
                            </div>
                            <span className={`px-2 py-1 text-xs rounded ${
                              ride.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {ride.status}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        {ride.is_day_booking ? (
                          /* Day booking: show start/end times */
                          <>
                            <div>
                              Start: {new Date(ensureUTC(ride.pickup_datetime)).toLocaleString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                            {ride.dropoff_datetime && (
                              <div>
                                End: {new Date(ride.dropoff_datetime).toLocaleString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                            )}
                          </>
                        ) : (
                          <div>
                            {new Date(ensureUTC(ride.pickup_datetime)).toLocaleString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        )}
                        <div>Rider: {ride.rider_name}</div>
                        <div>Distance: {(ride.distance_miles || 0).toFixed(1)} miles</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600 mb-1">
                        ${ride.status === 'cancelled_by_driver' ? '0.00' : (ride.driver_payout || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>Base Fare: ${(ride.total_fare || 0).toFixed(2)}</div>
                        <div className="text-red-600">
                          Platform Fee: -${(ride.platform_commission || ride.platform_fee || 0).toFixed(2)}
                        </div>
                        {ride.platform_fee_tax != null && ride.platform_fee_tax > 0 && (
                          <div className="text-red-600">
                            Sales Tax: -${ride.platform_fee_tax.toFixed(2)}
                          </div>
                        )}
                        {(ride.tip_amount || 0) > 0 && (
                          <div className="text-green-600">
                            Tip: +${(ride.tip_amount || 0).toFixed(2)}
                          </div>
                        )}
                      </div>
                      {ride.payment_flow_version === 2 &&
                       !ride.payment_captured &&
                       ride.tip_window_expires_at && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="text-xs text-yellow-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>
                              Tip window active - Payment processing in {calculateTimeRemaining(ride.tip_window_expires_at)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Giving your rider time to add a tip. Auto-processes after 24 hours.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RideHistory;